import React, { FC, useEffect, useRef } from 'react';

import { Box } from '@mui/system';
import { Typography } from '@mui/material';
import { useStripe } from '@stripe/react-stripe-js';

import {
  getPharmacyBilling,
  paymentActions,
  PaymentPlans,
  paymentResetValue,
  PharmacyBillingPageStatuses,
  pharmacyBillingSelector,
  resetStatus,
  setPaymentLoad,
  StripePaymentStatus,
  successSelector,
  upgradePackage,
  upgradePackageResult,
} from '@pharmaplan/common';

import SubscriptionDialogs from '../../ExistingPaymentPlan/SubscriptionDialogs/SubscriptionDialogs';
import { showError } from '../../../../../Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import usePaymentCheckStatus from '../../../../../../hooks/Payment/usePaymentCheckStatus';
import useUpdateCardDetails from '../../../../../../hooks/Payment/useUpdateCardDetails';
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../../../hooks/useAppSelector';
import { resetDialog } from '../../../../../../reducers/dialogReducer';
import { Constants } from '../../../../../../helpers/Constants';
import CustomButton from '../../../../../common/CustomButton';
import strings from '../../../../../../localization';

import styles from '../styles';

/*
  WHEN USER PRESSES UPGRADE, SERVER ATTEMPTS TO UPGRADE FROM BACK-END
  IF CHALLENGE IS REQUIRED (3DS) THEN SERVER WILL SEND THE SECRET TO FRONTEND
  IN WHICH CASE THE FRONTEND CAN COMPLETE THE PAYMENT FROM FRONT-END
*/

interface IUpgradePlanButton {
  setPageStatus: React.Dispatch<
    React.SetStateAction<PharmacyBillingPageStatuses>
  >;
}

const MAX_CHECKS = 10;

const UpgradePlanButton: FC<IUpgradePlanButton> = ({ setPageStatus }) => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const { checkPaymentStatus } = usePaymentCheckStatus();

  const result = useAppSelector(upgradePackageResult);
  const billing = useAppSelector(pharmacyBillingSelector);
  const apiSuccessfullyCompleted = useAppSelector((state) =>
    successSelector([paymentActions.upgradePackage], state));

  const { updatePaymentMethod } = useUpdateCardDetails();
  const maxChecks = useRef(0);
  const keepChecking = useRef(false);
  const planRef = useRef<string | null>('');

  const { unlimitedFees, currentPlan } = billing ?? {};
  const { success, secret } = result ?? {};

  const upgradeToUnlimited = () => {
    dispatch(upgradePackage());
  };

  const { showOnUpgrade } = SubscriptionDialogs({ setPageStatus });

  const clearData = () => {
    dispatch(paymentResetValue(['upgradeResult']));
    dispatch(resetStatus([paymentActions.upgradePackage]));
    dispatch(setPaymentLoad(false));
    maxChecks.current = 0;
    keepChecking.current = false;
  };

  const handleErrors = (message: string) => {
    clearData();
    showError(dispatch, message);
    keepChecking.current = false;
  };

  const keepCheckingUnlimited = () => {
    dispatch(getPharmacyBilling()).then(() => {
      if (maxChecks.current > MAX_CHECKS) {
        handleErrors(strings.paymentInProgressIssue);
        return;
      }

      if (planRef.current?.toLowerCase?.() === PaymentPlans.unlimited) {
        clearData();
        dispatch(resetDialog());
        setPageStatus(PharmacyBillingPageStatuses.existingPaymentPlan);
        return;
      }
      if (keepChecking.current) {
        maxChecks.current += 1;
        setTimeout(keepCheckingUnlimited, 1000);
      }
    });
  };

  const onSuccess = () => {
    keepCheckingUnlimited();
    keepChecking.current = true;
  };

  useEffect(() => {
    if (success) {
      dispatch(setPaymentLoad(true));
      onSuccess();
    } else if (!success && apiSuccessfullyCompleted) {
      dispatch(setPaymentLoad(true));
      stripe?.retrievePaymentIntent(secret).then((res) => {
        if (res?.error) {
          handleErrors(res.error.message ?? '');
          return;
        }

        const { paymentIntent } = res ?? {};
        if (paymentIntent?.status === StripePaymentStatus.requiresAction) {
          stripe
            ?.handleNextAction({ clientSecret: secret })
            .then(checkPaymentStatus(onSuccess, clearData))
            .catch((err: any) => {
              handleErrors((err as { message: string })?.message);
            });
        } else {
          checkPaymentStatus(onSuccess, clearData)(res);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, apiSuccessfullyCompleted]);

  useEffect(() => {
    planRef.current = currentPlan;
  }, [currentPlan]);

  return (
    <Box>
      <Box sx={styles.priceContainer}>
        <Box sx={styles.infoContainer}>
          <Typography sx={styles.label}>{strings.payNow}</Typography>
          <Typography sx={[styles.label, styles.value]}>
            {`$${unlimitedFees} + ${strings.tax}`}
          </Typography>
        </Box>
      </Box>
      <CustomButton
        label={strings.confirmAndPay}
        onClick={showOnUpgrade(upgradeToUnlimited)}
      />
      <Box sx={styles.buttonContainer}>
        <CustomButton
          label={strings.updateCardDetails}
          onClick={updatePaymentMethod}
          variant={Constants.variant.text}
          customButtonStyle={styles.buttonText}
        />
      </Box>
    </Box>
  );
};

export default UpgradePlanButton;
